import React, { memo, useEffect, useRef } from "react";
import _ from "lodash";
import { useI18n } from "Hooks";
import { Trans } from "react-i18next";
import classNames from "classnames";

import { Alert, Button } from "@toddle-edu/ds-web";

import { generateRandomId } from "Utils";
import {
  getIsLtiLaunchFailSafeEnabled,
  DIRECT_LTI_LAUNCH_SERVICES,
} from "LTIComponent/modules/Utils";

import classes from "./LTIIframe.scss";

const LTIDirectLaunch = ({ initialLaunchUrl, headerData }) => {
  const formRef = useRef(null);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.submit();
    }
  }, []);

  return (
    <form
      method="POST"
      action={initialLaunchUrl}
      ref={formRef}
      style={{ display: "none" }}
    >
      {_.map(Object.entries(headerData), ([key, value]) => (
        <input key={key} name={key} value={value} type="hidden" />
      ))}
    </form>
  );
};

const shouldLaunchDirectly = (serviceName, isLTIMobileView) => {
  return DIRECT_LTI_LAUNCH_SERVICES.includes(serviceName) && isLTIMobileView;
};

const LTIIframe = ({
  showModal,
  initialLaunchUrl,
  headerData,
  onFrameLoaded,
  isLTIMobileView,
  serviceName,
}) => {
  const iframeContextID = generateRandomId();
  const { t } = useI18n();

  const submitOnMount = form => {
    if (!form) return;
    form.submit();
  };

  const handleOpenInNewWindow = () => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = initialLaunchUrl;
    form.target = "_blank";

    _.forEach(headerData, (value, key) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const isLtiLaunchFailSafeEnabled = getIsLtiLaunchFailSafeEnabled();

  const LtiLaunchLink = ({ onClick }) => {
    return (
      <span
        className={classNames(
          "text-body",
          "text-primary",
          classes.inlineLaunchText
        )}
      >
        <Trans i18nKey="classRoom:launch_lti_tool_in_new_tab">
          <Button variant="link-destructive" onClick={onClick} />
          <span />
        </Trans>
      </span>
    );
  };

  const directLaunch = shouldLaunchDirectly(serviceName, isLTIMobileView);

  if (directLaunch) {
    return (
      <div className={showModal ? classes.ltiContent : classes.fullWidth}>
        <LTIDirectLaunch
          initialLaunchUrl={initialLaunchUrl}
          headerData={headerData}
        />
      </div>
    );
  }

  return (
    <div className={showModal ? classes.ltiContent : classes.fullWidth}>
      {isLtiLaunchFailSafeEnabled && (
        <div className={classes.alertContainer}>
          <Alert
            type={"info"}
            title={t("classRoom:lti_content_not_visible_title")}
            message={
              !isLTIMobileView
                ? t("classRoom:lti_content_not_visible_subtitle")
                : null
            }
            actionElement={
              isLTIMobileView ? (
                <LtiLaunchLink onClick={handleOpenInNewWindow} />
              ) : (
                <Button variant="primary" onClick={handleOpenInNewWindow}>
                  {t("integration:launch_lti")}
                </Button>
              )
            }
            isClosable={false}
            variant={"inline"}
            actionElementPosition={isLTIMobileView ? "bottom" : "right"}
            dsVersion={"2.0"}
          />
        </div>
      )}

      <form
        method="POST"
        id="lti-form"
        target={`iframe_${iframeContextID}`}
        action={initialLaunchUrl}
        ref={submitOnMount}
      >
        {_.map(Object.entries(headerData), ([key, value]) => (
          <input key={key} name={key} value={value} type="hidden" />
        ))}
      </form>
      <iframe
        title={
          serviceName
            ? t("a11y:lti_tool_with_name", { serviceName })
            : t("a11y:lti_tool_content")
        }
        src="about:blank"
        name={`iframe_${iframeContextID}`}
        onLoad={onFrameLoaded}
        width="100%"
        height={isLtiLaunchFailSafeEnabled ? "90%" : "100%"}
        allow="autoplay *; geolocation *; microphone *; camera *; midi *; encrypted-media *"
        allowFullScreen="true"
      ></iframe>
    </div>
  );
};

// Use React.memo to memoize the iframe so that it wont rerender
export default memo(LTIIframe);
